import React, { useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';

import Form from './Form';
import graph from './graph';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { isEmptyObject } from 'helpers/formatObject';
import { useLazyQuery, useMutation } from '@apollo/client';
import { NewDialog, NewDialogTitle, NewDialogContent, NewDialogActions } from 'components';

export default function UpdatePopup({ ids, refetch, title = 'ویژگی' }) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [formError, setFormError] = useState(true);
  const { userToken } = useSelector((state) => state.auth);
  const [fetchModel, { loading }] = useLazyQuery(graph.get.query);
  const [updateModel, { loading: updating }] = useMutation(graph.update.query);
  const [deleteModel, { loading: deleting }] = useMutation(graph.delete.query);

  const onOpen = () => setOpen(true);
  const onClose = () => setOpen(false);

  const getModel = async () => {
    try {
      const { data } = await fetchModel({
        context: {
          serviceName: graph.get.serviceName,
          headers: {
            authorization: `Bearer ${userToken}`,
          },
        },
        variables: {
          ids,
          for_admin: 1,
        },
      });
      if (!isEmptyObject(data)) {
        const res = data[graph.get.name].data[0];
        const { attributes } = res;
        if (res) {
          setFormData({
            attribute_value_product_shop_items: attributes
              .map((attribute) =>
                attribute.values.map((value) => ({
                  attribute_value_id: value?.id,
                  price_change_value: value.price_change_value,
                }))
              )
              .flat(),
          });
        }
      }
    } catch (error) {}
  };

  const onChange = ({ formData, errors }) => {
    setFormData(formData);
    setFormError(Boolean(errors.length > 0));
  };

  const onSubmit = async () => {
    try {
      const { data, errors } = await updateModel({
        context: {
          serviceName: graph.update.serviceName,
          headers: {
            authorization: `Bearer ${userToken}`,
          },
        },
        variables: {
          attribute_value_product_shop_items: formData?.attribute_value_product_shop_items.map((attribute) => ({
            product_shop_id: ids,
            attribute_value_id: String(attribute?.attribute_value_id),
            price_change_value: String(attribute?.price_change_value),
          })),
        },
      });
      if (!errors) {
        refetch();
        onClose();
        if (!isEmptyObject(data)) {
          data[graph.update.name]?.messages.map((message) => toast.success(String(message)));
        }
      }
    } catch (error) {
      setFormData(formData);
    }
  };

  const onDelete = async () => {
    try {
      const { data, errors } = await deleteModel({
        context: {
          serviceName: graph.delete.serviceName,
          headers: {
            authorization: `Bearer ${userToken}`,
          },
        },
        variables: {
          product_shop_id: ids,
        },
      });
      if (!errors) {
        refetch();
        onClose();
        if (!isEmptyObject(data)) {
          data[graph.delete.name]?.messages.map((message) => toast.success(String(message)));
        }
      }
    } catch (error) {
      setFormData(formData);
    }
  };

  useEffect(() => {
    open && getModel();
  }, [open]);

  return (
    <>
      <Button
        onClick={onOpen}
        size="small"
        color="secondary"
        variant="contained"
        startIcon={<ListAltRoundedIcon fontSize="small" />}
      >
        {title}
      </Button>
      <NewDialog label="update" open={open} onClose={onClose}>
        <NewDialogTitle title={title} onClose={onClose} />
        <NewDialogContent isPopup>
          <Stack p={1} flex={2} rowGap={1} bgcolor="background.paper">
            {loading ? (
              <Stack rowGap={2} height="100%" alignItems="center" justifyContent="center">
                <CircularProgress size={20} color="inherit" />
                <Typography align="center" variant="caption" color="text.disabled">
                  در حال بارگذاری...
                </Typography>
              </Stack>
            ) : (
              <Form formData={formData} onChange={onChange} />
            )}
          </Stack>
        </NewDialogContent>
        <NewDialogActions>
          <Button
            size="large"
            color="error"
            variant="contained"
            onClick={onDelete}
            sx={{ minWidth: 80 }}
            disabled={loading || deleting}
          >
            <FormattedMessage id="delete" />
          </Button>
          <Button
            autoFocus
            size="large"
            color="primary"
            variant="contained"
            onClick={onSubmit}
            sx={{ minWidth: 80 }}
            disabled={loading || updating || formError}
          >
            <FormattedMessage id="update" />
          </Button>
        </NewDialogActions>
      </NewDialog>
    </>
  );
}
